import { useEffect } from "react";
import { ArrowDown, ArrowUp, Copy, Plus, Trash2 } from "lucide-react";
import { useFieldArray } from "react-hook-form";
import type { Control, FieldPath } from "react-hook-form";

import type { SignaturePreparationForm } from "@/routes/_app/pessoa/-utils/signature-preparation";
import { SignaturePreparation } from "@/routes/_app/pessoa/-utils/signature-preparation";
import type { SignatureRequirement, SignatureTerm } from "@/types/signatures";

import { SendTermsFieldInput } from "./field-input";

function itemCountLabel(count: number) {
	if (count === 1) {
		return "item";
	}

	return "itens";
}

export function SendTermsPatterns({
	terms,
	requirements,
	control,
	disabled,
}: {
	terms: SignatureTerm[];
	requirements: SignatureRequirement[];
	control: Control<SignaturePreparationForm>;
	disabled: boolean;
}) {
	const patterns = terms.flatMap((term) => {
		const requirement = requirements.find((candidate) => candidate.termId === term.id);

		return (requirement?.patterns ?? []).map((pattern) => ({ term, pattern }));
	});

	if (patterns.length === 0) {
		return null;
	}

	return (
		<section className="surface-panel">
			<div className="border-b border-border px-4 py-3">
				<h3 className="text-sm font-semibold text-foreground">Listas do documento</h3>
			</div>

			<div className="flex flex-col gap-5 p-4">
				{patterns.map(({ term, pattern }) => (
					<SendTermsPattern
						key={`${term.id}_${pattern.patternId}`}
						termId={term.id}
						termName={terms.length > 1 ? term.name : undefined}
						pattern={pattern}
						control={control}
						disabled={disabled}
					/>
				))}
			</div>
		</section>
	);
}

function SendTermsPattern({
	termId,
	termName,
	pattern,
	control,
	disabled,
}: {
	termId: number;
	termName: string | undefined;
	pattern: SignatureRequirement["patterns"][number];
	control: Control<SignaturePreparationForm>;
	disabled: boolean;
}) {
	const key = SignaturePreparation.patternKey(termId, pattern.patternId);
	const name: `patternValues.${string}` = `patternValues.${key}`;
	const items = useFieldArray({ control, name, keyName: "formKey" });

	useEffect(() => {
		if (items.fields.length >= pattern.minimumItems) {
			return;
		}

		items.append({ itemId: crypto.randomUUID(), values: {} });
	}, [items, pattern.minimumItems]);

	return (
		<div
			data-slot="term-pattern"
			data-pattern-id={pattern.patternId}
			className="flex flex-col gap-3"
		>
			<div className="flex items-start justify-between gap-3">
				<div>
					<p className="text-sm font-medium text-foreground">{pattern.label}</p>
					{termName !== undefined && <p className="text-xs text-muted-foreground">{termName}</p>}
				</div>
				<span className="text-xs tabular-nums text-muted-foreground">
					{items.fields.length} {itemCountLabel(items.fields.length)}
				</span>
			</div>

			{items.fields.map((item, index) => (
				<div key={item.formKey} className="rounded-lg border border-border bg-background/35 p-3">
					<div className="mb-3 flex items-center justify-between gap-3">
						<span className="text-xs font-semibold text-muted-foreground">Item {index + 1}</span>
						<div className="flex items-center gap-1">
							<PatternAction
								label="Mover para cima"
								disabled={disabled || index === 0}
								onClick={() => items.move(index, index - 1)}
							>
								<ArrowUp />
							</PatternAction>
							<PatternAction
								label="Mover para baixo"
								disabled={disabled || index === items.fields.length - 1}
								onClick={() => items.move(index, index + 1)}
							>
								<ArrowDown />
							</PatternAction>
							<PatternAction
								label="Duplicar item"
								disabled={disabled}
								onClick={() =>
									items.insert(index + 1, {
										itemId: crypto.randomUUID(),
										values: { ...item.values },
									})
								}
							>
								<Copy />
							</PatternAction>
							<PatternAction
								label="Remover item"
								disabled={disabled || items.fields.length <= pattern.minimumItems}
								onClick={() => items.remove(index)}
							>
								<Trash2 />
							</PatternAction>
						</div>
					</div>

					<div className="grid gap-3 sm:grid-cols-2">
						{pattern.fields.map((field) => (
							<SendTermsFieldInput
								key={field.fieldId}
								field={field}
								name={
									`patternValues.${key}.${index}.values.${field.fieldId}` as FieldPath<SignaturePreparationForm>
								}
								control={control}
								disabled={disabled}
							/>
						))}
					</div>
				</div>
			))}

			<button
				type="button"
				data-slot="pattern-add"
				disabled={disabled}
				onClick={() => items.append({ itemId: crypto.randomUUID(), values: {} })}
				className="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-dashed border-primary/40 text-xs font-medium text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
			>
				<Plus className="size-4" />
				Adicionar item
			</button>
		</div>
	);
}

function PatternAction({
	label,
	disabled,
	onClick,
	children,
}: {
	label: string;
	disabled: boolean;
	onClick(): void;
	children: React.ReactNode;
}) {
	return (
		<button
			type="button"
			aria-label={label}
			disabled={disabled}
			onClick={onClick}
			className="flex size-7 items-center justify-center rounded-md text-muted-foreground hover:bg-foreground/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-30 [&_svg]:size-3.5"
		>
			{children}
		</button>
	);
}
